/**
 * Theme Toggle Component
 * 
 * A modern theme toggle component that uses MUI's data attribute approach
 * for CSS variables and color scheme switching.
 */

import React from 'react';
import {
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Box,
  useTheme
} from '@mui/material';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SystemModeIcon
} from '@mui/icons-material';
import { useColorScheme } from '@mui/material/styles';

/**
 * Simple Icon Toggle - switches between light/dark/system
 */
export function ThemeIconToggle() {
  const { mode, setMode } = useColorScheme();

  if (!mode) {
    return null; // Still loading
  }

  const handleToggle = () => {
    if (mode === 'light') {
      setMode('dark');
    } else if (mode === 'dark') {
      setMode('system');
    } else {
      setMode('light');
    }
  };

  const getIcon = () => {
    switch (mode) {
      case 'light':
        return <LightModeIcon />;
      case 'dark':
        return <DarkModeIcon />;
      case 'system':
        return <SystemModeIcon />;
      default:
        return <SystemModeIcon />;
    }
  };

  const getTooltip = () => {
    switch (mode) {
      case 'light':
        return 'Switch to dark mode';
      case 'dark':
        return 'Switch to system mode';
      case 'system':
        return 'Switch to light mode';
      default:
        return 'Toggle theme';
    }
  };

  return (
    <Tooltip title={getTooltip()}>
      <IconButton onClick={handleToggle} color="inherit">
        {getIcon()}
      </IconButton>
    </Tooltip>
  );
}

/**
 * Switch Toggle - simple light/dark switch
 */
export function ThemeSwitchToggle({ label = "Dark Mode" }) {
  const { mode, setMode } = useColorScheme();

  if (!mode) {
    return null; // Still loading
  }

  const isDarkMode = mode === 'dark';

  const handleChange = (event) => {
    setMode(event.target.checked ? 'dark' : 'light');
  };

  return (
    <FormControlLabel
      control={
        <Switch
          checked={isDarkMode}
          onChange={handleChange}
          color="primary"
        />
      }
      label={label}
    />
  );
}

/**
 * Select Dropdown - full control with system option
 */
export function ThemeSelectToggle({ label = "Theme Mode" }) {
  const { mode, setMode } = useColorScheme();

  if (!mode) {
    return null; // Still loading
  }

  const handleChange = (event) => {
    setMode(event.target.value);
  };

  return (
    <FormControl size="small" sx={{ minWidth: 120 }}>
      <InputLabel id="theme-select-label">{label}</InputLabel>
      <Select
        labelId="theme-select-label"
        value={mode}
        label={label}
        onChange={handleChange}
      >
        <MenuItem value="system">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SystemModeIcon fontSize="small" />
            System
          </Box>
        </MenuItem>
        <MenuItem value="light">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LightModeIcon fontSize="small" />
            Light
          </Box>
        </MenuItem>
        <MenuItem value="dark">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DarkModeIcon fontSize="small" />
            Dark
          </Box>
        </MenuItem>
      </Select>
    </FormControl>
  );
}

/**
 * Default export - the icon toggle
 */
export default ThemeIconToggle;
